import React from 'react';

import { Box, Stack, Tooltip, Typography, alpha } from '@mui/material';

import ConfidenceBadge from './ConfidenceBadge';
import { agg, money, moneyShort, pct } from './logic';
import { tcc } from './palette';

type Agg = ReturnType<typeof agg>;

/** Small swatch + label used as the key for the booked / predicted pair everywhere on the page. */
export function SeriesKey({ kind, label }: { kind: 'booked' | 'predicted' | 'breakeven'; label: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} component="span" sx={{ whiteSpace: 'nowrap' }}>
      {kind === 'breakeven' ? (
        <Box component="span" sx={{ width: 14, height: 0, borderTop: `2px dashed ${tcc.breakeven}` }} />
      ) : (
        <Box
          component="span"
          sx={{
            width: 10,
            height: 10,
            borderRadius: '2px',
            bgcolor: tcc[kind],
            border: '1px solid',
            borderColor: 'text.secondary',
          }}
        />
      )}
      <Typography component="span" variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}

/**
 * Capacity track: booked load factor (dark), the extra seats predicted to sell by handback (light) and the
 * break-even load factor (tick), so one bar shows where a group of departures is and where it is heading.
 */
export function LoadFactorBar({
  lf,
  plf,
  beLf,
  height = 10,
  currentOnly = false,
}: {
  lf: number | null;
  plf: number | null;
  beLf: number | null;
  height?: number;
  currentOnly?: boolean;
}) {
  const clamp = (v: number | null) => Math.max(0, Math.min(1, v ?? 0)) * 100;
  const booked = clamp(lf);
  const predicted = Math.max(booked, clamp(plf));
  const label = currentOnly
    ? `Current load factor ${pct(lf)}`
    : `Load Factor booked ${pct(lf)}, predicted ${pct(plf)}${beLf != null ? `, break-even ${pct(beLf)}` : ''}`;
  return (
    <Tooltip title={label}>
      <Box
        role="img"
        aria-label={label}
        sx={{ position: 'relative', height, borderRadius: height / 2, bgcolor: tcc.track, my: 0.5 }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            width: `${predicted}%`,
            bgcolor: tcc.predicted,
            borderRadius: height / 2,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            width: `${booked}%`,
            bgcolor: tcc.booked,
            border: (theme) => (theme.palette.mode === 'dark' ? '1px solid #c9d7e5' : undefined),
            borderRadius: height / 2,
            // 2px surface gap where booked meets predicted.
            boxShadow: (theme) => (predicted > booked ? `2px 0 0 0 ${theme.palette.background.paper}` : 'none'),
          }}
        />
        {beLf != null && (
          <Box
            sx={{
              position: 'absolute',
              left: `calc(${clamp(beLf)}% - 1px)`,
              top: -3,
              bottom: -3,
              width: 2,
              bgcolor: tcc.breakeven,
              borderRadius: 1,
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
}

function MarginValue({ value, rate, short }: { value: number; rate: number | null; short?: boolean }) {
  return (
    <>
      <Box component="span" sx={{ color: 'inherit' }}>
        {short ? moneyShort(value) : money(value)}
      </Box>
      <Typography component="span" variant="caption" display="block" sx={{ color: 'text.secondary', fontWeight: 600 }}>
        {pct(rate)}
      </Typography>
    </>
  );
}

export const costTitle = (supplier: number, total: number | null) =>
  total != null && total - supplier > 1
    ? `Supplier ${money(supplier)} + ICS at cost-plus ${money(total - supplier)}`
    : `Supplier cost ${money(supplier)}`;

/** "Margin known for X of Y departures" when some departures have no known margin (no price or no sliding scale). */
export const marginCoverage = (known: number, of: number) =>
  of > known ? `Margin known for ${known.toLocaleString('en-AU')} of ${of.toLocaleString('en-AU')} departures` : null;

/**
 * Region card body: metric names on the left, CURRENT BOOKED NUMBERS in the middle column and PREDICTED AT HANDBACK
 * on the right, so the eye can scan the real position and then the predicted one on every line.
 */
export function BookedPredictedGrid({ a }: { a: Agg }) {
  const lines: Array<{ label: string; booked: React.ReactNode; predicted: React.ReactNode }> = [
    { label: 'Load Factor', booked: pct(a.lf), predicted: pct(a.plf) },
    {
      label: 'Travellers',
      booked: `${Math.round(a.pax).toLocaleString('en-AU')} / ${a.cap.toLocaleString('en-AU')}`,
      predicted: Math.round(a.ppax).toLocaleString('en-AU'),
    },
    { label: 'TTV', booked: moneyShort(a.ttv), predicted: moneyShort(a.pttv) },
    {
      label: 'Margin',
      booked: <MarginValue value={a.m} rate={a.mp} short />,
      predicted: <MarginValue value={a.pmv} rate={a.pm} short />,
    },
  ];
  const head = { fontSize: '0.68rem', fontWeight: 700, letterSpacing: 0.3, textTransform: 'none' as const };
  return (
    <Box
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: 'minmax(64px, auto) minmax(0, 1fr) minmax(0, 1fr)',
        columnGap: 0.5,
        alignItems: 'center',
        fontVariantNumeric: 'tabular-nums',
        '& > .booked': { bgcolor: alpha(tcc.booked, 0.06) },
        '& > .predicted': { bgcolor: alpha(tcc.predicted, 0.14) },
        '& > .booked, & > .predicted': { textAlign: 'center', py: 0.5, px: 0.5, minWidth: 0 },
      }}
    >
      <span />
      <Box className="booked" sx={{ borderTop: `3px solid ${tcc.booked}`, borderRadius: '4px 4px 0 0' }}>
        <Typography sx={{ ...head, color: tcc.bookedText, minHeight: 0 }}>Current Booked Numbers</Typography>
      </Box>
      <Box className="predicted" sx={{ borderTop: `3px dashed ${tcc.predicted}`, borderRadius: '4px 4px 0 0' }}>
        <Typography sx={{ ...head, color: tcc.predictedText, minHeight: 0 }}>Predicted at Handback</Typography>
      </Box>
      <Box sx={{ gridColumn: '1 / -1', px: 0.5, pt: 0.75 }}>
        <LoadFactorBar lf={a.lf} plf={a.plf} beLf={a.beLf} />
        {/* Always rendered, so every region card has the same rows and cards line up side by side. */}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.68rem' }}>
          Break-Even Load Factor {a.beLf != null ? pct(a.beLf) : '—'}
        </Typography>
      </Box>
      {lines.map((line, index) => (
        <React.Fragment key={line.label}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: 'left', whiteSpace: 'nowrap', pr: 0.5, py: 0.5 }}
          >
            {line.label}
          </Typography>
          <Box
            className="booked"
            sx={{
              typography: 'body2',
              whiteSpace: 'nowrap',
              fontSize: index === 1 ? 11 : undefined,
              fontWeight: index === 0 ? 700 : 600,
              borderRadius: index === lines.length - 1 ? '0 0 4px 4px' : 0,
            }}
          >
            {line.booked}
          </Box>
          <Box
            className="predicted"
            sx={{
              typography: 'body2',
              whiteSpace: 'nowrap',
              fontSize: index === 1 ? 11 : undefined,
              fontWeight: index === 0 ? 700 : 600,
              borderRadius: index === lines.length - 1 ? '0 0 4px 4px' : 0,
            }}
          >
            {line.predicted}
          </Box>
        </React.Fragment>
      ))}
    </Box>
  );
}

/** One headline number with its label, used in the top summary. */
function Stat({ label, value, detail }: { label: string; value: React.ReactNode; detail?: React.ReactNode }) {
  return (
    <Box sx={{ minWidth: 0, fontVariantNumeric: 'tabular-nums' }}>
      <Typography variant="caption" color="text.secondary" display="block" noWrap>
        {label}
      </Typography>
      <Typography variant="h6" component="div" sx={{ lineHeight: 1.25, whiteSpace: 'nowrap' }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.3, minHeight: 0 }}>
        {detail || '\u00a0'}
      </Typography>
    </Box>
  );
}

/**
 * The selection's headline numbers: CURRENT BOOKED NUMBERS on the left, PREDICTED AT HANDBACK on the right, the same
 * four measures in the same order on both sides.
 */
export function BookedPredictedSummary({ a }: { a: Agg }) {
  const side = (kind: 'booked' | 'predicted') => {
    const booked = kind === 'booked';
    return (
      <Box
        sx={{
          p: { xs: 1.5, md: 2 },
          borderRadius: 1,
          bgcolor: alpha(tcc[kind], booked ? 0.06 : 0.14),
          borderTop: `3px ${booked ? 'solid' : 'dashed'} ${tcc[kind]}`,
          minWidth: 0,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          flexWrap="wrap"
          sx={{ mb: 1 }}
        >
          <Typography
            sx={{
              fontSize: '0.9rem',
              bgcolor: alpha(tcc[kind], 0.12),
              borderRadius: 1,
              px: 1,
              py: 0.75,
              fontWeight: 700,
              letterSpacing: 0.3,
              textTransform: 'none',
              color: booked ? tcc.bookedText : tcc.predictedText,
              mb: 1,
            }}
          >
            {booked ? 'Current Booked Numbers' : 'Predicted at Handback'}
          </Typography>
          {!booked && (
            <Box component="span" sx={{ fontSize: 13 }}>
              Confidence: <ConfidenceBadge tier={a.confidenceTier} aggregate />
            </Box>
          )}
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' },
            gap: 1.5,
          }}
        >
          <Stat
            label="Load Factor"
            value={pct(booked ? a.lf : a.plf)}
            detail={`${Math.round(booked ? a.pax : a.ppax).toLocaleString('en-AU')} of ${a.cap.toLocaleString('en-AU')} seats`}
          />
          <Stat label="TTV" value={moneyShort(booked ? a.ttv : a.pttv)} />
          <Stat
            label="Cost"
            value={
              <Tooltip
                title={
                  booked
                    ? costTitle(a.supplier, a.cost)
                    : a.psupplier
                      ? costTitle(a.psupplier, a.pcost)
                      : 'Supplier cost and ICS at cost-plus'
                }
              >
                <span>{moneyShort(booked ? a.cost : a.pcost)}</span>
              </Tooltip>
            }
            detail="supplier + ICS"
          />
          <Stat
            label="Margin"
            value={moneyShort(booked ? a.m : a.pmv)}
            detail={
              <>
                <Box component="span" sx={{ color: booked ? tcc.bookedText : tcc.predictedText, fontWeight: 600 }}>
                  {pct(booked ? a.mp : a.pm)} of TTV
                </Box>
                {marginCoverage(booked ? a.marginKnown : a.predMarginKnown, booked ? a.marginOf : a.predMarginOf) && (
                  <Box
                    component="span"
                    data-testid="margin-coverage"
                    sx={{ display: 'block', color: 'text.secondary' }}
                  >
                    {marginCoverage(booked ? a.marginKnown : a.predMarginKnown, booked ? a.marginOf : a.predMarginOf)}
                  </Box>
                )}
              </>
            }
          />
        </Box>
        <Box data-testid="summary-bar" sx={{ mt: 1.5 }}>
          <LoadFactorBar
            lf={a.lf}
            plf={booked ? a.lf : a.plf}
            beLf={booked ? null : a.beLf}
            height={8}
            currentOnly={booked}
          />
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          {booked ? (
            '\u00a0'
          ) : (
            <>
              Break-Even Load Factor {pct(a.beLf)} · {a.final.toLocaleString('en-AU')} departures past handback count as
              booked
            </>
          )}
        </Typography>
      </Box>
    );
  };
  return (
    <>
      {side('booked')}
      {side('predicted')}
    </>
  );
}
